import React from 'react';
import { subDays, Locale } from 'date-fns';
import { Chart } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ChartType,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
    TooltipOptions,
    Filler,
    ScriptableContext,
    LineController,
} from 'chart.js';
import { DateTime } from 'twain-sport-fe-mono/enums/DateTime';
import { formatDate } from 'twain-sport-fe-mono/utils/formatDate';
import { replaceAll } from 'twain-sport-fe-mono/utils/replaceAll';
import { translationProvider } from 'twain-sport-fe-mono/store/translations';
import { IParticipantDetailed } from 'twain-sport-fe-mono/interfaces';
import { getDateFnsLocale, setupDateFnsLocale } from 'twain-sport-fe-mono/utils';
import { Select } from 'twain-sport-fe-mono/components/Select';
import classes from './ParticipantProfileRankHistory.module.scss';

interface IProps {
    rankHistory: IParticipantDetailed['rankHistory'];
    isMobileView: boolean;
    locale?: Locale;
    themeColors?: { colorAccent: string; colorChartGradientRgb: string };
}

interface IDateRankItem {
    dateString: string;
    dateStringShort: string;
    date: Date;
    rank: IParticipantDetailed['rank'];
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
    Filler,
    LineController,
);

const DAYS_SELECTION = [30, 60, 90];

export const ParticipantProfileRankHistory: React.FC<IProps> = (props) => {
    const t = translationProvider;
    const [daySelection, setDaySelection] = React.useState<number>(DAYS_SELECTION[0]);
    const chartDataRef = React.useRef({ chartWidth: 0, chartHeight: 0, gradient: null });
    const chartTooltipRef = React.useRef<HTMLDivElement>(null);
    const [locale, setLocale] = React.useState<Locale>();

    const rankHistory = React.useMemo<IDateRankItem[]>(() => {
        if (!locale) {
            return [];
        }

        const currentDate = new Date();

        return Array.from({ length: daySelection }, (_, i) => i).map((value) => {
            const date = subDays(currentDate, value);
            const dateString = formatDate(date, DateTime.DATE_FORMAT);

            return {
                dateString,
                dateStringShort: formatDate(date, DateTime.DATE_FORMAT, { locale }),
                date,
                rank: props.rankHistory[dateString] ?? null,
            };
        });
    }, [props.rankHistory, daySelection, locale]);

    const daysSelection = React.useMemo(
        () =>
            DAYS_SELECTION.map((value) => ({
                name: replaceAll(t.string('{{x}}_days'), { x: value }),
                value,
            })),
        [],
    );

    const tooltipDraw = React.useCallback<TooltipOptions['external']>((context) => {
        if (!chartTooltipRef.current) {
            return;
        }

        if (!context.tooltip.opacity) {
            chartTooltipRef.current.style.opacity = '0';
            return;
        }

        const [value] = context.tooltip.body[0].lines;
        chartTooltipRef.current.innerText = value;
        chartTooltipRef.current.style.opacity = '1';
        chartTooltipRef.current.style.left = `${context.tooltip.caretX}px`;
        chartTooltipRef.current.style.top = `${context.tooltip.caretY}px`;
    }, []);

    const { colorAccent, colorChartGradientRgb } = React.useMemo(() => {
        chartDataRef.current.gradient = null;

        if (props.themeColors) {
            return props.themeColors;
        }

        const { style } = document.documentElement;

        return {
            colorAccent: `rgb(${style.getPropertyValue('--secondary-rgb')})`,
            colorChartGradientRgb: style.getPropertyValue('--color-chart-gradient-rgb'),
        };
    }, [props.themeColors]);

    React.useEffect(() => {
        if (props.locale) {
            setLocale(props.locale);
            return;
        }
        setupDateFnsLocale('en').then(() => {
            setLocale(getDateFnsLocale());
        });
    }, [props.locale]);

    const [careerHigh, careerLow] = React.useMemo(() => {
        const rankHistoryValues = rankHistory
            .filter((rankItem) => rankItem.rank !== null)
            .map((rankItem) => rankItem.rank);

        return [Math.min(...rankHistoryValues), Math.max(...rankHistoryValues)];
    }, [rankHistory]);

    if (!locale) {
        return null;
    }

    const fontSize = Number(props.isMobileView ? classes.fontSizeMobile : classes.fontSizeDesktop);

    let stepSizeY = 10;
    if (careerLow <= 10) {
        stepSizeY = 2;
    } else if (careerLow <= 20) {
        stepSizeY = 5;
    }

    const maxY = Math.ceil((careerLow + 1) / 10) * 10;

    return (
        <>
            <h3 className={classes.title}>{t.string('ranking_history')}</h3>
            <div className={classes.careerSummary}>
                <Select
                    className={classes.daysSelect}
                    options={daysSelection}
                    value={daySelection}
                    onChange={(value) => setDaySelection(value as number)}
                    dataQa="select-profile-selectDays"
                />
                <span className={classes.careerItem}>
                    {t.string('career_high')} <span className={classes.badge}>#{careerHigh}</span>
                </span>
                <span className={classes.careerItem}>
                    {t.string('career_low')} <span className={classes.badge}>#{careerLow}</span>
                </span>
            </div>
            <div className={classes.chartWrapper} data-qa="block-profile-historyRankChart">
                <p className={classes.chartLabel}>{t.string('rank')}</p>
                <Chart
                    type="line"
                    data={{
                        datasets: [
                            {
                                data: rankHistory.map((rankItem) => rankItem.rank),
                                tension: 0.5,
                                fill: 'start',
                                backgroundColor: (ctx: ScriptableContext<ChartType>) => {
                                    const chartWidth =
                                        ctx.chart.chartArea.left - ctx.chart.chartArea.right;
                                    const chartHeight =
                                        ctx.chart.chartArea.bottom - ctx.chart.chartArea.top;

                                    if (
                                        !chartDataRef.current.gradient ||
                                        chartDataRef.current.chartWidth !== chartWidth ||
                                        chartDataRef.current.chartHeight !== chartHeight
                                    ) {
                                        const maxValuePosition = chartHeight * (careerHigh / maxY);

                                        const gradient = ctx.chart.ctx.createLinearGradient(
                                            0,
                                            ctx.chart.chartArea.top + maxValuePosition,
                                            0,
                                            ctx.chart.chartArea.bottom,
                                        );
                                        gradient.addColorStop(
                                            0,
                                            `rgba(${colorChartGradientRgb}, 0.3)`,
                                        );
                                        gradient.addColorStop(
                                            0.9,
                                            `rgba(${colorChartGradientRgb}, 0)`,
                                        );

                                        chartDataRef.current.gradient = gradient;
                                        chartDataRef.current.chartWidth = chartWidth;
                                        chartDataRef.current.chartHeight = chartHeight;
                                    }

                                    return chartDataRef.current.gradient;
                                },
                                borderColor: colorAccent,
                                borderWidth: 1,
                                pointRadius: 2.5,
                                pointBorderWidth: 0,
                                pointBackgroundColor: colorAccent,
                                pointHitRadius: 8,
                                spanGaps: true,
                                clip: {
                                    left: 5,
                                    top: 5,
                                    right: 5,
                                    bottom: 5,
                                },
                            },
                        ],
                        labels: rankHistory.map((rankItem) => rankItem.dateStringShort),
                    }}
                    options={{
                        responsive: true,
                        aspectRatio: 1.8,
                        plugins: {
                            legend: {
                                display: false,
                            },
                            tooltip: {
                                enabled: false,
                                position: 'nearest',
                                external: tooltipDraw,
                            },
                        },
                        layout: {
                            padding: {
                                right: 10,
                            },
                        },
                        interaction: {
                            axis: 'xy',
                            mode: 'nearest',
                        },
                        scales: {
                            y: {
                                min: 1,
                                max: maxY,
                                reverse: true,
                                grid: {
                                    color: null,
                                },
                                ticks: {
                                    precision: 1,
                                    stepSize: stepSizeY,
                                    color: classes.colorLabel,
                                    font: {
                                        size: fontSize,
                                    },
                                },
                            },
                            x: {
                                reverse: true,
                                grid: {
                                    color: `rgba(${classes.colorAxisGridRgb}, 0.2)`,
                                    borderWidth: 1,
                                    tickBorderDash: [2, 4],
                                    borderColor: `rgba(${classes.colorAxisGridRgb}, 0.5)`,
                                    borderDash: [2, 4],
                                    offset: true,
                                },
                                ticks: {
                                    autoSkipPadding: 10,
                                    minRotation: 45,
                                    maxRotation: 45,
                                    color: classes.colorLabel,
                                    font: {
                                        size: fontSize,
                                    },
                                    align: 'end',
                                },
                            },
                        },
                    }}
                />
                <div ref={chartTooltipRef} className={classes.chartTooltip} />
            </div>
        </>
    );
};
